import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Tooltip as LMTooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, dateFmt } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useServiceAlerts } from "@/hooks/useServiceAlerts";
import { STATUS_LABELS, URGENCY_LABELS } from "@/lib/constants";
import {
    Crosshair, X, Volume2, VolumeX, Truck, AlertTriangle, Activity,
    Radio, MapPin, BellRing, Maximize2, Minimize2, RefreshCw, ExternalLink
} from "lucide-react";

// Iconos Leaflet por defecto
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const UNIT_COLOR = {
    disponible: "#10b981",
    ocupada: "#ef4444",
    mantenimiento: "#f59e0b",
    fuera_servicio: "#64748b",
};

function unitIcon(color) {
    return L.divIcon({
        className: "",
        html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:12px;font-family:IBM Plex Mono,monospace;">A</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
    });
}

const URGENCY_RING = {
    critica: "ring-2 ring-red-500 shadow-[0_0_20px_rgba(239,68,68,0.45)]",
    alta: "ring-2 ring-amber-400",
    normal: "ring-1 ring-slate-700",
    baja: "ring-1 ring-slate-800",
};

const URGENCY_BADGE = {
    critica: "bg-red-600 text-white",
    alta: "bg-amber-500 text-slate-900",
    normal: "bg-sky-500 text-white",
    baja: "bg-slate-600 text-white",
};

// Estatus considerados "en operación viva" (lo que cabina debe mirar)
const LIVE_STATUSES = new Set([
    "solicitud_recibida", "pendiente_cotizacion", "cotizado", "autorizado",
    "asignando_unidad", "unidad_asignada", "tripulacion_notificada",
    "en_salida", "en_ruta_origen", "arribo_origen", "valoracion_inicial",
    "paciente_abordo", "en_ruta_destino", "arribo_destino", "entrega_paciente"
]);

// Estatus que debería atenderse YA (sin unidad asignada)
const NEEDS_ATTENTION = new Set([
    "solicitud_recibida", "pendiente_cotizacion", "asignando_unidad"
]);

function timeSince(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `hace ${Math.floor(diff)}s`;
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    return dateFmt(iso);
}

export default function FocusCabin() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [units, setUnits] = useState([]);
    const [services, setServices] = useState([]);
    const [now, setNow] = useState(new Date());
    const [lastRefresh, setLastRefresh] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const rootRef = useRef(null);

    const { unreadCount, audioEnabled, enableAudio, markAllRead } = useServiceAlerts(user);

    // Reloj cada segundo
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Polling 10s
    const load = async () => {
        try {
            const [u, s] = await Promise.all([
                api.get("/units"),
                api.get("/dashboard/operational"),
            ]);
            if (u?.data) setUnits(u.data);
            if (s?.data?.services) setServices(s.data.services);
            setLastRefresh(new Date());
        } catch (e) {
            console.warn("focus load failed", e);
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 10_000);
        return () => clearInterval(t);
    }, []);

    // Escape para salir
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") navigate("/dispatch");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [navigate]);

    const toggleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                await rootRef.current?.requestFullscreen?.();
                setFullscreen(true);
            } else {
                await document.exitFullscreen?.();
                setFullscreen(false);
            }
        } catch (e) {
            console.warn("fullscreen failed", e?.message);
        }
    };

    const live = useMemo(() => {
        return services
            .filter((s) => LIVE_STATUSES.has(s.status))
            .sort((a, b) => {
                // criticas primero, luego pending-attention, luego por created_at desc
                const ua = ["critica", "alta", "normal", "baja"].indexOf(a.urgency_level || "normal");
                const ub = ["critica", "alta", "normal", "baja"].indexOf(b.urgency_level || "normal");
                if (ua !== ub) return ua - ub;
                const aAtt = NEEDS_ATTENTION.has(a.status) ? 0 : 1;
                const bAtt = NEEDS_ATTENTION.has(b.status) ? 0 : 1;
                if (aAtt !== bAtt) return aAtt - bAtt;
                return new Date(b.created_at) - new Date(a.created_at);
            });
    }, [services]);

    const counts = useMemo(() => {
        return {
            pending: services.filter((s) => NEEDS_ATTENTION.has(s.status)).length,
            assigned: services.filter((s) => ["unidad_asignada", "tripulacion_notificada"].includes(s.status)).length,
            enRoute: services.filter((s) => ["en_salida", "en_ruta_origen", "arribo_origen", "valoracion_inicial", "paciente_abordo", "en_ruta_destino"].includes(s.status)).length,
            atDest: services.filter((s) => ["arribo_destino", "entrega_paciente"].includes(s.status)).length,
            critical: services.filter((s) => s.urgency_level === "critica" && LIVE_STATUSES.has(s.status)).length,
            unitsAvail: units.filter((u) => u.status === "disponible").length,
            unitsBusy: units.filter((u) => u.status === "ocupada").length,
            unitsTotal: units.length,
        };
    }, [services, units]);

    const center = [19.4326, -99.1332]; // CDMX
    const mapUnits = units.filter((u) => u.lat && u.lng);
    const mapServices = live.filter((s) => s.origin_lat && s.origin_lng);

    return (
        <div
            ref={rootRef}
            className="fixed inset-0 z-[9999] bg-slate-950 text-white flex flex-col font-sans select-none"
            data-testid="focus-cabin"
        >
            {/* ============ TOP BAR ============ */}
            <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-900/80 backdrop-blur">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center">
                        <Crosshair className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div>
                        <div className="font-display font-black text-lg leading-none tracking-tight uppercase">
                            Modo Concentración
                        </div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
                            Cabina · {user?.name}
                        </div>
                    </div>
                </div>

                {/* Reloj gigante */}
                <div className="text-center">
                    <div className="font-mono tabular-nums text-5xl font-black tracking-tight leading-none">
                        {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
                        {now.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        {lastRefresh && (
                            <span className="ml-2">· Última sync {timeSince(lastRefresh.toISOString())}</span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => audioEnabled ? null : enableAudio()}
                        className={`w-9 h-9 rounded flex items-center justify-center transition-colors ${audioEnabled ? "bg-emerald-600 hover:bg-emerald-500" : "bg-slate-800 hover:bg-slate-700 text-slate-300"}`}
                        title={audioEnabled ? "Audio activo" : "Activar audio"}
                        data-testid="focus-audio-toggle"
                    >
                        {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={load}
                        className="w-9 h-9 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300"
                        title="Refrescar ahora"
                        data-testid="focus-refresh-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={toggleFullscreen}
                        className="w-9 h-9 rounded bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300"
                        title={fullscreen ? "Salir pantalla completa" : "Pantalla completa"}
                        data-testid="focus-fullscreen-btn"
                    >
                        {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => navigate("/dispatch")}
                        className="flex items-center gap-2 px-3 h-9 rounded bg-red-600 hover:bg-red-500 text-white text-xs font-bold uppercase tracking-wider"
                        title="Salir (Esc)"
                        data-testid="focus-exit-btn"
                    >
                        <X className="w-4 h-4" /> Salir
                    </button>
                </div>
            </header>

            {/* ============ KPI STRIP ============ */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 px-5 py-3 border-b border-slate-800 bg-slate-900/50">
                <KPI label="Por asignar" value={counts.pending} accent={counts.pending > 0 ? "text-red-400" : "text-slate-400"} icon={AlertTriangle} testid="kpi-pending" />
                <KPI label="Asignados" value={counts.assigned} accent="text-sky-300" icon={Truck} testid="kpi-assigned" />
                <KPI label="En ruta" value={counts.enRoute} accent="text-amber-300" icon={Activity} testid="kpi-enroute" />
                <KPI label="En destino" value={counts.atDest} accent="text-emerald-300" icon={MapPin} testid="kpi-atdest" />
                <KPI label="Críticos" value={counts.critical} accent={counts.critical > 0 ? "text-red-500" : "text-slate-400"} icon={BellRing} pulse={counts.critical > 0} testid="kpi-critical" />
                <KPI label="Unid. disp." value={`${counts.unitsAvail}/${counts.unitsTotal}`} accent="text-emerald-300" icon={Radio} testid="kpi-units" />
            </div>

            {/* ============ MAIN GRID ============ */}
            <div className="flex-1 grid grid-cols-12 gap-3 p-3 overflow-hidden">

                {/* Panel izquierdo · servicios en vivo */}
                <section className="col-span-5 xl:col-span-4 bg-slate-900 border border-slate-800 rounded flex flex-col overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
                        <h2 className="font-display font-bold text-xs uppercase tracking-widest">Servicios en operación · {live.length}</h2>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllRead}
                                className="text-[10px] uppercase tracking-widest font-bold bg-red-600 hover:bg-red-500 px-2 py-1 rounded animate-pulse"
                                data-testid="focus-alerts-clear"
                            >
                                {unreadCount} nuevos · marcar leídos
                            </button>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto fm-scroll p-2 space-y-2" data-testid="focus-services-list">
                        {live.length === 0 && (
                            <div className="text-center py-12 text-slate-500 text-sm">
                                <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                Sin servicios activos. Tablero limpio.
                            </div>
                        )}
                        {live.map((s) => (
                            <ServiceCard key={s.id} s={s} />
                        ))}
                    </div>
                </section>

                {/* Mapa central */}
                <section className="col-span-7 xl:col-span-5 bg-slate-900 border border-slate-800 rounded overflow-hidden relative">
                    <div className="absolute top-2 left-3 z-[500] flex items-center gap-2 bg-slate-900/80 backdrop-blur px-2.5 py-1 rounded text-[10px] uppercase tracking-widest font-bold">
                        <Radio className="w-3 h-3 text-emerald-400" /> Live · {mapUnits.length} unidades · {mapServices.length} servicios
                    </div>
                    <MapContainer center={center} zoom={11} className="h-full w-full" attributionControl={false} zoomControl={true}>
                        <TileLayer
                            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                            subdomains="abcd"
                            maxZoom={19}
                        />
                        {mapUnits.map((u) => (
                            <Marker
                                key={u.id}
                                position={[u.lat, u.lng]}
                                icon={unitIcon(UNIT_COLOR[u.status] || "#64748b")}
                            >
                                <Popup>
                                    <div className="font-sans text-slate-900">
                                        <div className="font-mono font-bold">{u.economic_number}</div>
                                        <div className="text-xs">{u.ambulance_type}</div>
                                        <div className="text-[11px] text-slate-500">{u.status}</div>
                                    </div>
                                </Popup>
                                <LMTooltip permanent direction="top" offset={[0, -18]} className="!bg-slate-900 !text-white !border-none !rounded !text-[10px] !font-mono !shadow-lg">
                                    {u.economic_number}
                                </LMTooltip>
                            </Marker>
                        ))}
                        {mapServices.map((s) => (
                            <CircleMarker
                                key={`s-${s.id}`}
                                center={[s.origin_lat, s.origin_lng]}
                                radius={s.urgency_level === "critica" ? 10 : 7}
                                fillColor={s.urgency_level === "critica" ? "#ef4444" : s.urgency_level === "alta" ? "#f59e0b" : "#0ea5e9"}
                                color="white"
                                weight={2}
                                fillOpacity={0.85}
                            >
                                <Popup>
                                    <div className="font-sans text-slate-900">
                                        <div className="font-mono font-bold">{s.folio}</div>
                                        <div className="text-xs">{s.patient_name}</div>
                                        <div className="text-[11px] text-slate-500">{STATUS_LABELS[s.status]}</div>
                                    </div>
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>
                </section>

                {/* Panel derecho · unidades disponibles + alertas */}
                <aside className="col-span-12 xl:col-span-3 bg-slate-900 border border-slate-800 rounded flex flex-col overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-800 border-b border-slate-700">
                        <h2 className="font-display font-bold text-xs uppercase tracking-widest">Flota · {counts.unitsAvail} disp. / {counts.unitsBusy} ocupadas</h2>
                    </div>
                    <div className="flex-1 overflow-y-auto fm-scroll p-2 space-y-1.5" data-testid="focus-units-list">
                        {units
                            .slice()
                            .sort((a, b) => {
                                const order = ["disponible", "ocupada", "mantenimiento", "fuera_servicio"];
                                return order.indexOf(a.status) - order.indexOf(b.status);
                            })
                            .map((u) => (
                                <div key={u.id} className="bg-slate-800/60 border border-slate-700 rounded px-3 py-2 flex items-center justify-between" data-testid={`focus-unit-${u.economic_number}`}>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`w-2.5 h-2.5 rounded-full ${u.status === "disponible" ? "bg-emerald-400" : u.status === "ocupada" ? "bg-red-500" : u.status === "mantenimiento" ? "bg-amber-400" : "bg-slate-500"}`}
                                        />
                                        <div>
                                            <div className="font-mono font-bold text-xs">{u.economic_number}</div>
                                            <div className="text-[9px] text-slate-400 uppercase">{u.ambulance_type}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] text-slate-300 uppercase font-bold">{u.status}</div>
                                        {u.last_ping_at && (
                                            <div className="text-[9px] text-slate-500 font-mono">GPS {timeSince(u.last_ping_at)}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        {units.length === 0 && (
                            <div className="text-center py-8 text-slate-500 text-xs">
                                Sin unidades dadas de alta.
                            </div>
                        )}
                    </div>
                    <div className="border-t border-slate-700 p-2 grid grid-cols-2 gap-2">
                        <Link to="/services/new" className="bg-red-600 hover:bg-red-500 text-white text-[10px] uppercase tracking-widest font-bold text-center py-2 rounded" data-testid="focus-new-service">
                            + Nueva solicitud
                        </Link>
                        <Link to="/dispatch" className="bg-slate-700 hover:bg-slate-600 text-white text-[10px] uppercase tracking-widest font-bold text-center py-2 rounded" data-testid="focus-go-dispatch">
                            Tablero clásico
                        </Link>
                    </div>
                </aside>
            </div>

            {/* footer mini */}
            <footer className="px-5 py-1.5 border-t border-slate-800 bg-slate-900/80 text-[10px] text-slate-500 flex items-center justify-between">
                <span className="font-mono">FindMed Dispatch · Modo Concentración</span>
                <span>Esc para salir · refresca cada 10s</span>
            </footer>
        </div>
    );
}

function KPI({ label, value, accent, icon: Icon, pulse, testid }) {
    return (
        <div className={`bg-slate-900 border border-slate-800 rounded px-3 py-2 flex items-center gap-3 ${pulse ? "animate-pulse" : ""}`} data-testid={testid}>
            <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.5} />
            <div className="min-w-0">
                <div className={`font-mono tabular-nums font-black text-2xl leading-none ${accent}`}>{value}</div>
                <div className="text-[9px] text-slate-400 uppercase tracking-widest mt-0.5">{label}</div>
            </div>
        </div>
    );
}

function ServiceCard({ s }) {
    const urgency = s.urgency_level || "normal";
    const ring = URGENCY_RING[urgency];
    const needsAttention = NEEDS_ATTENTION.has(s.status);

    return (
        <Link
            to={`/services/${s.id}`}
            className={`block bg-slate-800/60 hover:bg-slate-800 rounded p-3 transition-colors ${ring}`}
            data-testid={`focus-service-${s.folio}`}
        >
            <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-mono font-black text-sm">{s.folio}</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${URGENCY_BADGE[urgency]}`}>
                            {URGENCY_LABELS[urgency]}
                        </span>
                        {needsAttention && (
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-700 text-white animate-pulse">
                                Atender
                            </span>
                        )}
                    </div>
                    <div className="font-semibold text-sm truncate mt-1">{s.patient_name || "Sin paciente"}</div>
                    <div className="text-[11px] text-slate-400 truncate">{s.client_name || "Sin cliente"}</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
            </div>

            <div className="text-[10px] text-slate-300 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-500" strokeWidth={1.5} />
                <span className="truncate">{s.origin_address || "Sin dirección"}</span>
            </div>

            <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-slate-700">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                    {STATUS_LABELS[s.status] || s.status}
                </span>
                <div className="flex items-center gap-2">
                    {s.unit_number && (
                        <span className="text-[10px] font-mono font-bold text-emerald-300 flex items-center gap-1">
                            <Truck className="w-3 h-3" /> {s.unit_number}
                        </span>
                    )}
                    <span className="text-[10px] text-slate-500 font-mono">{timeSince(s.created_at)}</span>
                </div>
            </div>
        </Link>
    );
}
